import { useEffect, useId, useMemo, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import type { GooglePlace } from "@mtform/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { DateField } from "@/components/form/DateField";
import { TimeField } from "@/components/form/TimeField";
import { AddressField } from "@/components/form/AddressField";
import { AddressPickHint } from "@/components/form/AddressPickHint";
import { AddressConfirmedHint } from "@/components/form/AddressConfirmedHint";
import { TRIP_EDIT_MIN_LEAD_HOURS } from "@mtform/shared";
import {
  formatIsoDateForDisplay,
  formatTimeForDisplay,
  fullAddressFromPlace,
  isSameAddressLine,
  isWallClockBeyondEditLeadNY,
  normalizeDateForPayload,
  needsAmPm,
  normalizeTimeForPayload,
} from "@/lib/format";

// =============================================================================
// EditTripDialog
//
// Dispatch tool for editing a trip's time and/or addresses while it's still
// in pending / assigned / will-call status and pickup is beyond the shared
// edit lead time (TRIP_EDIT_MIN_LEAD_HOURS). Trip
// mode (pickup vs appointment) is locked to whatever the trip was booked
// in — the dialog exposes one time field accordingly so dispatch can't
// accidentally flip a trip's anchor.
//
// Date + time use the same DateField + TimeField the booking form uses
// (calendar popover + masked typing) instead of the browser's native
// datetime-local, so the form's look stays consistent across screens.
// =============================================================================

export interface EditTripPayload {
  /** Sent as "YYYY-MM-DDTHH:MM" when pickupTime changed. */
  pickupTime?: string;
  /** Sent as "YYYY-MM-DDTHH:MM" when appointmentTime changed. */
  appointmentTime?: string;
  pickupAddress?: string;
  pickupCityZip?: string;
  pickupPlace?: GooglePlace;
  destinationAddress?: string;
  destinationCityZip?: string;
  destinationPlace?: GooglePlace;
}

interface EditTripDialogProps {
  open: boolean;
  friendlyId: string;
  patientName: string;
  /** Existing pickup ISO UTC (`2026-05-12T00:40:28Z`). Always present
   *  on editable trips. Used to pre-fill the pickup-mode form. */
  pickupAtIso?: string;
  /** Existing appointment wall-clock in dropoff tz, no tz suffix
   *  (`2026-05-12T02:25:00`). Present only on appointment-mode trips —
   *  presence tells the dialog which time field to render. */
  appointmentAtIso?: string;
  /** Will-call trips don't have a fixed pickup time. When this is
   *  true the dialog hides the time row entirely and the Save
   *  button only enables on an address change. */
  isWillCall: boolean;
  /** Admin callers (the Bookings page) drive the dialog from our own
   *  bookings-table snapshot, which stores the schedule as separate NY
   *  wall-clock strings rather than the ISO timestamps the live Booked
   *  Trips feed carries. When `initialSchedule` is set, its pre-split
   *  date (MM/DD/YYYY) + time (HH:MM, or "") seed the form verbatim with
   *  NO UTC/NY conversion, and `scheduleMode` decides which time field
   *  the edit writes. Omit both to keep the ISO-derived behaviour the
   *  live list uses. */
  initialSchedule?: { date: string; time: string };
  scheduleMode?: "pickup" | "appointment";
  /** The trip's CURRENT pickup / destination, so the dialog can warn when an
   *  edit would leave the two the same address (the residence left out). The
   *  live feed carries a single combined string (no separate city/zip); the
   *  admin list passes street + city/zip separately. Omit to skip the warning
   *  (a caller that can't supply them just doesn't get the nudge). */
  currentPickupAddress?: string;
  currentPickupCityZip?: string;
  currentDestinationAddress?: string;
  currentDestinationCityZip?: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (payload: EditTripPayload) => void;
}

export function EditTripDialog({
  open,
  friendlyId,
  patientName,
  pickupAtIso,
  appointmentAtIso,
  isWillCall,
  initialSchedule,
  scheduleMode,
  currentPickupAddress,
  currentPickupCityZip,
  currentDestinationAddress,
  currentDestinationCityZip,
  pending,
  onCancel,
  onConfirm,
}: EditTripDialogProps) {
  const confirm = useConfirm();
  const dateId = useId();
  const timeId = useId();
  const pickupAddrId = useId();
  const dropAddrId = useId();

  // Mode is fixed per trip:
  //   - will-call: no time row at all (will-call has no fixed pickup
  //     time to edit; only the addresses can change). Editing the
  //     time would silently flip the trip out of the will-call lane.
  //   - appointment-anchored: time row writes `appointmentTime`.
  //   - pickup-anchored: time row writes `pickupTime`.
  // Switching modes via /update is technically allowed by the
  // backend but we don't expose that here to minimize foot-guns.
  // Mode is normally inferred from which ISO field the live feed carries;
  // admin callers pass `scheduleMode` explicitly because their
  // bookings-table rows have no ISO timestamp to infer from.
  const overrideDate = initialSchedule?.date;
  const overrideTime = initialSchedule?.time;
  const isAppointmentMode =
    !isWillCall &&
    (scheduleMode ? scheduleMode === "appointment" : Boolean(appointmentAtIso));
  const showTimeRow = !isWillCall;
  const fieldLabel = isAppointmentMode ? "Appointment time" : "Pickup time";

  // Pre-filled values. Live Booked Trips: pickup is UTC, convert to NY
  // local; appointment is wall-clock-without-tz, use as-is. Admin
  // Bookings: `initialSchedule` is already NY wall-clock (date + time of
  // day from our own snapshot), so it seeds the form verbatim with no tz
  // math. All paths land in the MM/DD/YYYY + HH:MM display format that
  // DateField/TimeField expect.
  const initial = useMemo(() => {
    if (overrideDate != null) {
      return { date: overrideDate, time: overrideTime ?? "" };
    }
    if (isAppointmentMode && appointmentAtIso) {
      return splitWallClock(appointmentAtIso);
    }
    if (pickupAtIso) {
      return splitNyLocal(pickupAtIso);
    }
    return { date: "", time: "" };
  }, [overrideDate, overrideTime, isAppointmentMode, pickupAtIso, appointmentAtIso]);

  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(seedTime(initial.time));

  const [pickupAddr, setPickupAddr] = useState("");
  const [pickupCityZip, setPickupCityZip] = useState("");
  const [pickupPlace, setPickupPlace] = useState<GooglePlace | null>(null);

  const [dropAddr, setDropAddr] = useState("");
  const [dropCityZip, setDropCityZip] = useState("");
  const [dropPlace, setDropPlace] = useState<GooglePlace | null>(null);

  // Reset on open so a previous edit's leftover state doesn't bleed into
  // the next trip's form. Date+time snap back to the initial values (so
  // they show the current schedule, ready to be tweaked).
  useEffect(() => {
    if (!open) return;
    setDate(initial.date);
    setTime(seedTime(initial.time));
    setPickupAddr("");
    setPickupCityZip("");
    setPickupPlace(null);
    setDropAddr("");
    setDropCityZip("");
    setDropPlace(null);
  }, [open, initial.date, initial.time]);

  const normalizedDate = normalizeDateForPayload(date);
  const normalizedTime = normalizeTimeForPayload(time);
  // For will-call we don't render the time row at all, so the inputs
  // never need to pass the "both fields filled" check. Otherwise (a
  // scheduled trip) both date and time have to be present before
  // anything counts as a valid time edit.
  const timeFieldsValid = !showTimeRow || Boolean(normalizedDate && normalizedTime && !needsAmPm(time));

  // Compare to initial so an unchanged time doesn't count as a "change".
  // Backend filters redundant time edits too, but skipping them here keeps
  // the audit note clean and avoids a no-op rate recalc round-trip.
  const initialDateIso = normalizeDateForPayload(initial.date);
  const initialTime = normalizeTimeForPayload(initial.time);
  const timeChanged =
    showTimeRow &&
    timeFieldsValid &&
    (normalizedDate !== initialDateIso || normalizedTime !== initialTime);

  // A new pickup must still sit beyond the shared edit lead time. NOTE: the
  // backend does NOT enforce this (the comment here used to claim it did) —
  // this gate and the Edit button's are the only ones there are. The
  // comparison anchors to America/New_York since every Sinai trip is in that
  // timezone. (Doesn't apply on will-call: the time row is hidden.)
  const newPickupTooSoon =
    timeChanged &&
    !isWallClockBeyondEditLeadNY(normalizedDate, normalizedTime);

  // A changed address must carry coordinates before Save — the /update path
  // (unlike create) does NOT backfill them server-side, so a coordinate-less
  // edit would reach TripLink with no location. Leaving an address blank keeps
  // the trip's current one.
  const pickupAddrReady = !pickupAddr.trim() || hasFiniteCoords(pickupPlace);
  const dropAddrReady = !dropAddr.trim() || hasFiniteCoords(dropPlace);
  const somethingChanged =
    timeChanged || pickupAddr.length > 0 || dropAddr.length > 0;

  // What "leave blank" keeps, spelled out when the caller passed it. The live
  // feed sends one combined line; the admin list sends street + city/ZIP.
  const currentPickup = [currentPickupAddress, currentPickupCityZip].filter(Boolean).join(", ");
  const currentDrop = [currentDestinationAddress, currentDestinationCityZip]
    .filter(Boolean)
    .join(", ");
  const canSubmit =
    somethingChanged &&
    timeFieldsValid &&
    !newPickupTooSoon &&
    pickupAddrReady &&
    dropAddrReady;

  const payload = useMemo<EditTripPayload>(() => {
    const p: EditTripPayload = {};
    if (timeChanged) {
      const dt = `${normalizedDate}T${normalizedTime}`;
      if (isAppointmentMode) p.appointmentTime = dt;
      else p.pickupTime = dt;
    }
    if (pickupAddr && pickupPlace) {
      p.pickupAddress = pickupAddr;
      p.pickupCityZip = pickupCityZip;
      p.pickupPlace = pickupPlace;
    }
    if (dropAddr && dropPlace) {
      p.destinationAddress = dropAddr;
      p.destinationCityZip = dropCityZip;
      p.destinationPlace = dropPlace;
    }
    return p;
  }, [
    timeChanged,
    normalizedDate,
    normalizedTime,
    isAppointmentMode,
    pickupAddr,
    pickupCityZip,
    pickupPlace,
    dropAddr,
    dropCityZip,
    dropPlace,
  ]);

  async function handleConfirm() {
    if (!canSubmit) return;

    // Same-address guard, matching the booking form. Only fires when THIS edit
    // touches an address and the result leaves pickup == drop-off - so a
    // time-only edit (or a pre-existing same-address trip nobody's changing)
    // never nags. Effective address = the newly-typed one, or the trip's
    // current one when that side wasn't edited. Skipped when the caller didn't
    // supply the current addresses.
    const addressEdited = pickupAddr.trim().length > 0 || dropAddr.trim().length > 0;
    if (addressEdited) {
      const effPickupAddr = pickupAddr.trim() ? pickupAddr : currentPickupAddress ?? "";
      const effPickupCity = pickupAddr.trim() ? pickupCityZip : currentPickupCityZip ?? "";
      const effDropAddr = dropAddr.trim() ? dropAddr : currentDestinationAddress ?? "";
      const effDropCity = dropAddr.trim() ? dropCityZip : currentDestinationCityZip ?? "";
      if (isSameAddressLine(effPickupAddr, effPickupCity, effDropAddr, effDropCity)) {
        const proceed = await confirm({
          title: "Pickup and drop-off are the same address",
          description:
            "This edit leaves the pickup and drop-off at the same address. If the patient is going to or from home, the residence should be in one of them. Save anyway?",
          confirmText: "Yes, save",
          cancelText: "Let me fix it",
        });
        if (!proceed) return;
      }
    }

    onConfirm(payload);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onCancel();
      }}
    >
      <DialogContent
        hideCloseButton
        structured
        className="max-w-[520px]"
        onInteractOutside={(e) => {
          // Clicks on Google Places autocomplete suggestions (rendered into
          // a `.pac-container` portal *outside* the dialog) would otherwise
          // be treated as "interact outside → close." Swallow those so the
          // dialog stays open when a dispatcher picks an address.
          const target = e.target as HTMLElement | null;
          if (target?.closest(".pac-container")) {
            e.preventDefault();
            return;
          }
          if (pending) e.preventDefault();
        }}
      >
        <DialogHeader icon={Pencil}>
          <DialogTitle>Edit trip {friendlyId}</DialogTitle>
          <DialogDescription>
            {showTimeRow
              ? `Update ${patientName}'s ${fieldLabel.toLowerCase()} or addresses.`
              : `Update ${patientName}'s pickup or destination address. Will-call trips don't have a fixed pickup time to edit.`}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-5">
          {/* ─── Date + time (two labeled fields, mode locked) ──────────
              Hidden on will-call (those trips have no fixed pickup time).
              Date gets its OWN clearly labeled field (and leads, full
              width) so it reads as a thing you can change: moving a trip
              to another day is exactly the cancel-and-rebook step we want
              dispatch to skip. Previously both shared one "Pickup time"
              label, which hid the date. The lead-time error rides on the
              Date field so it shows on one line. */}
          {showTimeRow ? (
            <div className="flex flex-col gap-3">
              <Field
                label="Date"
                htmlFor={dateId}
                error={
                  newPickupTooSoon
                    ? `Must be at least ${TRIP_EDIT_MIN_LEAD_HOURS} hours from now.`
                    : undefined
                }
                hint={
                  newPickupTooSoon
                    ? undefined
                    : "Move the trip to another day without a cancel and rebook."
                }
              >
                <DateField
                  id={dateId}
                  value={date}
                  onChange={setDate}
                  disabled={pending}
                  futureOnly
                />
              </Field>
              <Field
                label={fieldLabel}
                htmlFor={timeId}
                hint={
                  isAppointmentMode
                    ? "Dispatch will back-time the pickup."
                    : "When dispatch should pick the patient up."
                }
              >
                <TimeField
                  id={timeId}
                  boxClassName="w-40"
                  value={time}
                  onChange={setTime}
                  disabled={pending}
                />
              </Field>
            </div>
          ) : null}
          {showTimeRow ? <div aria-hidden="true" className="h-px bg-border/60" /> : null}

          {/* ─── Pickup address ────────────────────────────────────────
              Same pattern as the booking form: a full canonical one-line
              address, with a quiet green "Location confirmed" once it has
              coordinates or an amber "pick from the dropdown" nudge until it
              does. No manual City/State/ZIP input. */}
          <Field
            label="New pickup address"
            htmlFor={pickupAddrId}
            hint={
              currentPickup ? (
                <>
                  Leave blank to keep <span className="text-foreground/80">{currentPickup}</span>.
                </>
              ) : (
                "Leave blank to keep the current address."
              )
            }
          >
            <AddressField
              id={pickupAddrId}
              value={pickupAddr}
              onChange={(v) => setPickupAddr(v)}
              onPlace={(place, cityZip) => {
                // Full canonical address, consistent with the booking form
                // (fullAddressFromPlace). cityZip is still captured for the
                // wire; coordinates are what make the edit dispatch.
                setPickupAddr(fullAddressFromPlace(place, pickupAddr));
                setPickupPlace(place);
                setPickupCityZip(cityZip);
              }}
              onClearPlace={() => {
                // Editing after a pick drops the coordinates but keeps the
                // typed text; Save stays disabled (and the pick hint shows)
                // until the address is re-picked, because /update does NOT
                // backfill coordinates server-side.
                setPickupPlace(null);
                setPickupCityZip("");
              }}
              placeholder="Start typing address…"
              disabled={pending}
            />
          </Field>
          {pickupAddr.trim() ? (
            hasFiniteCoords(pickupPlace) ? (
              <AddressConfirmedHint />
            ) : (
              <AddressPickHint />
            )
          ) : null}

          {/* ─── Destination address ──────────────────────────────────── */}
          <Field
            label="New destination address"
            htmlFor={dropAddrId}
            hint={
              currentDrop ? (
                <>
                  Leave blank to keep <span className="text-foreground/80">{currentDrop}</span>.
                </>
              ) : (
                "Leave blank to keep the current destination."
              )
            }
          >
            <AddressField
              id={dropAddrId}
              value={dropAddr}
              onChange={(v) => setDropAddr(v)}
              onPlace={(place, cityZip) => {
                // Full canonical address — see comment on pickup onPlace.
                setDropAddr(fullAddressFromPlace(place, dropAddr));
                setDropPlace(place);
                setDropCityZip(cityZip);
              }}
              onClearPlace={() => {
                // Mirror the pickup-side clear: drop the coordinates, keep the
                // typed text; Save stays disabled until re-picked.
                setDropPlace(null);
                setDropCityZip("");
              }}
              placeholder="Start typing address…"
              disabled={pending}
            />
          </Field>
          {dropAddr.trim() ? (
            hasFiniteCoords(dropPlace) ? (
              <AddressConfirmedHint />
            ) : (
              <AddressPickHint />
            )
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit || pending}>
            {pending ? (
              <>
                <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
                Saving…
              </>
            ) : (
              "Save changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/** True when a place carries usable (finite) coordinates. A changed address
 *  must have these before Save, since the /update path (unlike create) does
 *  not backfill them server-side. */
function hasFiniteCoords(p: GooglePlace | null): boolean {
  return Boolean(
    p &&
      p.lat != null &&
      p.lon != null &&
      Number.isFinite(p.lat) &&
      Number.isFinite(p.lon),
  );
}

/** A trip's own time comes as 24-hour "12:30", which the time box would ask
 *  AM or PM of like a typed one (needsAmPm). It's 12:30 PM: seeded as such. */
function seedTime(t: string): string {
  return needsAmPm(t) ? formatTimeForDisplay(t) : t;
}

/** Wall-clock string ("2026-05-15T14:00:00", no tz) → form parts. */
function splitWallClock(s: string): { date: string; time: string } {
  const [d, t] = s.split("T");
  const [y, m, day] = (d ?? "").split("-");
  const [h, min] = (t ?? "00:00").split(":");
  if (!y || !m || !day || !h || !min) return { date: "", time: "" };
  return {
    date: formatIsoDateForDisplay(`${y}-${m}-${day}`),
    time: `${h}:${min}`,
  };
}

/** UTC ISO ("2026-05-12T00:40:28Z") → NY-local form parts.
 *  Sinai pickups happen in America/New_York; the form's "PU time" is always
 *  rendered in that tz so dispatch sees what the patient sees regardless
 *  of the dispatcher's browser timezone. */
function splitNyLocal(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(d).map((p) => [p.type, p.value]),
  );
  if (!parts.year || !parts.month || !parts.day || !parts.hour || !parts.minute) {
    return { date: "", time: "" };
  }
  return {
    date: `${parts.month}/${parts.day}/${parts.year}`,
    // Intl returns "24" for midnight on some Chromiums; normalise.
    time: `${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}`,
  };
}
